// Forms tab: outputs of the ACA engine. Filing readiness → generate versioned
// form records from the snapshot → review read-only 1095-C / 1094-C → audited
// overrides → PDFs. No compliance logic lives here.
import { useMemo, useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertTriangle, CheckCircle2, ChevronLeft, ChevronRight, Download, ExternalLink, FileText, Info, Loader2, Lock, RefreshCw, ShieldAlert, Undo2,
} from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Form1095C, type Form1095CRecord } from "@/components/compliance/Form1095C";
import { Form1094C, type Form1094CTransmittal } from "@/components/compliance/Form1094C";
import { Help, TermLabel, Explained } from "@/pages/employer/aca/shared/Help";

interface ACAFormsTabProps {
  clientId?: number;
}

interface AcaException {
  code: string;
  severity: "blocking" | "warning" | "info";
  message: string;
  employeeId?: number;
  month?: number;
  fix?: { area: string; hint?: string };
}

interface ValidationResponse {
  snapshot: { id: number; version: number; status: string; computedAt: string | null };
  ready: boolean;
  employer: AcaException[];
  employees: Array<{ employeeId: number; name: string; formRequired: string | null; blocking: AcaException[]; warnings: AcaException[]; lineConsistency: number[] }>;
  form1094: { monthlyFtCount: number[]; monthlyTotalCount: number[]; monthlyMecOffered: boolean[]; line18: number; reconciles: boolean };
  ale: { isAle: boolean; averageFte: number; estimated: boolean; source: string };
  generatedVersion: number;
}

interface FormsResponse {
  version: number;
  records: Form1095CRecord[];
  transmittal: Form1094CTransmittal | null;
  generatedPdfs: Array<{ id: number; employeeId: number | null; formType: string; version: number; pdfUrl: string | null; generatedAt: string }>;
  client: any | null;
  employees: any[];
}

interface OverrideRow {
  id: number;
  employeeId: number | null;
  month: number | null;
  field: string;
  priorValue: string | null;
  newValue: string | null;
  reason: string;
  createdByUserId: string | null;
  createdAt: string;
  revokedAt?: string | null;
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const FIX_AREA_LABEL: Record<string, string> = {
  employees: "Employees tab", policies: "Policies tab", affordability: "Affordability tab", settings: "Settings tab",
  forms: "Forms tab", hr: "Employee record (HR)", benefits: "Benefits / plans", plan_library: "Plan library (superadmin)",
};

type OverrideField = "line14" | "line15" | "line16" | "plan_start_month" | "ft_count" | "total_count" | "mec_offered";

function fieldLabel(f: OverrideField | string): string {
  const map: Record<string, string> = {
    line14: "Line 14", line15: "Line 15", line16: "Line 16", plan_start_month: "Plan start month",
    ft_count: "1094-C full-time count", total_count: "1094-C total employee count", mec_offered: "1094-C MEC offer indicator",
  };
  return map[f] ?? f;
}

export default function ACAFormsTab({ clientId: propClientId }: ACAFormsTabProps) {
  const { user } = useAuth();
  const clientId = propClientId ?? (user as any)?.clientId ?? 1;
  const { toast } = useToast();
  const currentYear = new Date().getFullYear();
  const [taxYear, setTaxYear] = useState(currentYear - 1);
  const availableYears = Array.from({ length: currentYear - 2015 }, (_, i) => currentYear - 1 - i);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string>("");
  const [formView, setFormView] = useState<"1095c" | "1094c">("1095c");

  const [overrideOpen, setOverrideOpen] = useState(false);
  const [override, setOverride] = useState<{ field: OverrideField; employeeId: number | null; month: number | null; newValue: string; reason: string }>({
    field: "line14", employeeId: null, month: 1, newValue: "", reason: "",
  });

  const q = `clientId=${clientId}&taxYear=${taxYear}`;
  const invalidateAll = () => {
    for (const key of ["/api/aca/validation", "/api/aca/forms", "/api/aca/overrides", "/api/aca/year", "/api/aca/dashboard", "/api/aca/reporting-years"]) {
      queryClient.invalidateQueries({ queryKey: [key] });
    }
  };

  const { data: constants } = useQuery<any>({
    queryKey: ["/api/aca/constants", taxYear],
    queryFn: async () => { const r = await fetch(`/api/aca/constants?year=${taxYear}`, { credentials: "include" }); if (!r.ok) throw new Error("Failed to fetch constants"); return r.json(); },
  });
  const { data: validation, isLoading: validationLoading, error: validationError } = useQuery<ValidationResponse>({
    queryKey: ["/api/aca/validation", clientId, taxYear],
    queryFn: async () => {
      const r = await fetch(`/api/aca/validation?${q}`, { credentials: "include" });
      if (!r.ok) throw new Error((await r.json().catch(() => ({})))?.error || "Failed to load filing readiness");
      return r.json();
    },
    enabled: !!clientId,
  });
  const { data: forms, isLoading: formsLoading } = useQuery<FormsResponse>({
    queryKey: ["/api/aca/forms", clientId, taxYear],
    queryFn: async () => { const r = await fetch(`/api/aca/forms?${q}`, { credentials: "include" }); if (!r.ok) throw new Error("Failed to load forms"); return r.json(); },
    enabled: !!clientId,
  });
  const { data: overrides = [] } = useQuery<OverrideRow[]>({
    queryKey: ["/api/aca/overrides", clientId, taxYear],
    queryFn: async () => { const r = await fetch(`/api/aca/overrides?${q}`, { credentials: "include" }); if (!r.ok) throw new Error("Failed to load overrides"); return r.json(); },
    enabled: !!clientId,
  });
  const { data: reportingYears = [] } = useQuery<any[]>({
    queryKey: ["/api/aca/reporting-years", clientId],
    queryFn: async () => { const r = await fetch(`/api/aca/reporting-years?clientId=${clientId}`, { credentials: "include" }); if (!r.ok) throw new Error("Failed to load reporting years"); return r.json(); },
    enabled: !!clientId,
  });
  const { data: groupMembers = [] } = useQuery<any[]>({
    queryKey: ["/api/aca/ale-group-members", clientId],
    queryFn: async () => { const r = await fetch(`/api/aca/ale-group-members?clientId=${clientId}`, { credentials: "include" }); if (!r.ok) return []; return r.json(); },
    enabled: !!clientId,
  });

  const reportingYear = reportingYears.find((y) => y.taxYear === taxYear);
  const isLocked = !!reportingYear?.isLocked;

  const generateMutation = useMutation({
    mutationFn: async () => apiRequest("POST", "/api/aca/forms/generate", { clientId, taxYear }),
    onSuccess: async (res) => {
      const data = await res.json().catch(() => ({}));
      toast({ title: "Forms generated", description: `${data?.forms ?? ""} Form 1095-C record(s) plus the 1094-C for ${taxYear} (version ${data?.meta?.version ?? ""}).` });
      invalidateAll();
    },
    onError: (e: any) => toast({ title: "Generation failed", description: e.message, variant: "destructive" }),
  });

  const finalizeMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch("/api/aca/year/finalize", { method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include", body: JSON.stringify({ clientId, taxYear }) });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw Object.assign(new Error(data?.error || "Finalize failed"), { blocking: data?.blocking });
      return data;
    },
    onSuccess: (data: any) => { toast({ title: `Tax year ${taxYear} finalized`, description: `Snapshot v${data?.meta?.version} frozen; ${data?.forms} form record(s) generated.` }); invalidateAll(); },
    onError: (e: any) => toast({ title: "Cannot finalize", description: e.message, variant: "destructive" }),
  });

  const overrideMutation = useMutation({
    mutationFn: async () => apiRequest("POST", "/api/aca/overrides", {
      clientId, taxYear, employeeId: override.employeeId, month: override.month, field: override.field,
      newValue: override.newValue === "" ? null : override.newValue, reason: override.reason,
    }),
    onSuccess: () => {
      toast({ title: "Override recorded", description: "The year was recalculated with this override. Generate forms again to see it on the form records." });
      setOverrideOpen(false);
      setOverride((o) => ({ ...o, newValue: "", reason: "" }));
      invalidateAll();
    },
    onError: (e: any) => toast({ title: "Override rejected", description: e.message, variant: "destructive" }),
  });

  const revokeMutation = useMutation({
    mutationFn: async (id: number) => apiRequest("DELETE", `/api/aca/overrides/${id}?clientId=${clientId}`),
    onSuccess: () => { toast({ title: "Override revoked", description: "The year was recalculated. Generate forms again to update the records." }); invalidateAll(); },
    onError: (e: any) => toast({ title: "Revoke failed", description: e.message, variant: "destructive" }),
  });

  const savePdfMutation = useMutation({
    mutationFn: async (target: { kind: "1095c"; employeeId: number } | { kind: "1094c" }) =>
      apiRequest("POST", target.kind === "1095c" ? `/api/aca/forms/1095c/${target.employeeId}/pdf?${q}` : `/api/aca/forms/1094c/pdf?${q}`),
    onSuccess: async (res) => {
      const data = await res.json().catch(() => ({}));
      toast({ title: "PDF saved", description: `${data?.fileName ?? "PDF"} was filed under Documents.` });
      queryClient.invalidateQueries({ queryKey: ["/api/aca/forms"] });
      queryClient.invalidateQueries({ queryKey: ["/api/documents"] });
    },
    onError: (e: any) => toast({ title: "PDF generation failed", description: e.message, variant: "destructive" }),
  });

  // Part I identity comes embedded in the forms response (server-side, tenant-scoped).
  const client = forms?.client ?? null;
  const employeesData = forms?.employees ?? [];
  const employeesById = useMemo(() => new Map<number, any>(employeesData.map((e: any) => [e.id, e])), [employeesData]);
  const records = useMemo(() => {
    const rows = (forms?.records ?? []).map((record) => ({ record, employee: employeesById.get(record.employeeId) }));
    const name = (r: typeof rows[number]) => r.employee ? `${r.employee.lastName ?? ""} ${r.employee.firstName ?? ""}`.trim().toLowerCase() : `zzz ${r.record.employeeId}`;
    return rows.sort((a, b) => name(a).localeCompare(name(b)));
  }, [forms, employeesById]);

  const selected = useMemo(() => {
    if (!records.length) return null;
    if (!selectedEmployeeId) return records[0];
    return records.find((r) => String(r.record.employeeId) === selectedEmployeeId) ?? records[0];
  }, [records, selectedEmployeeId]);
  const currentIndex = selected ? records.findIndex((r) => r.record.employeeId === selected.record.employeeId) : 0;
  const goTo = (delta: number) => { const next = records[currentIndex + delta]; if (next) setSelectedEmployeeId(String(next.record.employeeId)); };

  const blockingCount = validation ? validation.employer.filter((e) => e.severity === "blocking").length + validation.employees.reduce((a, e) => a + e.blocking.length, 0) : 0;
  const warningCount = validation ? validation.employer.filter((e) => e.severity === "warning").length + validation.employees.reduce((a, e) => a + e.warnings.length, 0) : 0;
  const lineConsistencyIssues = validation ? validation.employees.filter((e) => e.lineConsistency.length > 0) : [];

  const openOverride = (field: OverrideField, employeeId: number | null, month: number | null, current?: string | null) => {
    setOverride({ field, employeeId, month, newValue: current ?? "", reason: "" });
    setOverrideOpen(true);
  };

  const pdfFor = (employeeId: number | null, formType: string) =>
    (forms?.generatedPdfs ?? []).filter((p) => p.employeeId === employeeId && p.formType === formType)
      .sort((a, b) => b.version - a.version || (b.generatedAt > a.generatedAt ? 1 : -1))[0];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-xl font-semibold">ACA Forms</h2>
          <p className="text-sm text-muted-foreground">Forms 1095-C and 1094-C generated from the employee-by-month ACA calculation</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={String(taxYear)} onValueChange={(v) => { setTaxYear(Number(v)); setSelectedEmployeeId(""); }}>
            <SelectTrigger className="w-[120px]"><SelectValue placeholder="Year" /></SelectTrigger>
            <SelectContent>{availableYears.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
          </Select>
          <Button
            variant="outline"
            onClick={() => generateMutation.mutate()}
            disabled={!validation || generateMutation.isPending || isLocked || validation.ready === false}
            title={validation && !validation.ready ? `${blockingCount} blocking exception(s) and ${warningCount} warning(s); resolve blocking items first` : isLocked ? "Year is finalized; unlock it from History to regenerate" : undefined}
          >
            {generateMutation.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
            Generate forms{validation ? ` (v${validation.snapshot.version})` : ""}
          </Button>
          <Help term="snapshotVersion" />
          <Button
            onClick={() => finalizeMutation.mutate()}
            disabled={!validation || !validation.ready || finalizeMutation.isPending || isLocked}
            title={isLocked ? "Already finalized" : !validation?.ready ? "Resolve blocking exceptions before finalizing" : undefined}
          >
            {finalizeMutation.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Lock className="h-4 w-4 mr-2" />}
            {isLocked ? "Finalized" : "Finalize year"}
          </Button>
          <Help term="finalize" />
        </div>
      </div>

      <Alert className="border-blue-200 bg-blue-50 dark:bg-blue-950 dark:border-blue-800">
        <Info className="h-4 w-4 text-blue-600" />
        <AlertDescription className="text-sm text-blue-800 dark:text-blue-200">
          Forms are generated from each employee's monthly ACA status and coverage history for {taxYear}. The server decides who needs a 1095-C
          (ACA full-time in any month, or enrolled in self-insured coverage). Administr does not file with the IRS.
        </AlertDescription>
      </Alert>

      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <CardTitle className="flex items-center gap-2 text-base">
              {validation?.ready ? <CheckCircle2 className="h-5 w-5 text-green-600" /> : <ShieldAlert className="h-5 w-5 text-amber-600" />}
              <TermLabel term="readiness">Filing readiness</TermLabel>
            </CardTitle>
            {validation && (
              <div className="flex items-center gap-2 text-xs text-muted-foreground flex-wrap">
                <Explained term="exceptionBlocking"><Badge variant={blockingCount ? "destructive" : "secondary"}>{blockingCount} blocking</Badge></Explained>
                <Explained term="exceptionWarning"><Badge variant="outline">{warningCount} warnings</Badge></Explained>
                <TermLabel term="snapshotVersion">Snapshot v{validation.snapshot.version} · {validation.snapshot.status}{validation.snapshot.computedAt ? ` · ${new Date(validation.snapshot.computedAt).toLocaleString()}` : ""}</TermLabel>
                {validation.generatedVersion > 0 && <Explained term="snapshotVersion"><Badge variant="outline">Forms v{validation.generatedVersion}</Badge></Explained>}
              </div>
            )}
          </div>
          <CardDescription>Employer data, employee identifiers, offers, affordability, Line 14/15/16 consistency and 1094-C monthly counts are checked before forms are generated.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {validationLoading && <div className="text-sm text-muted-foreground">Checking…</div>}
          {validationError && <Alert variant="destructive"><AlertTriangle className="h-4 w-4" /><AlertDescription>{(validationError as Error).message}</AlertDescription></Alert>}
          {validation && (
            <>
              {validation.employer.length === 0 && validation.employees.every((e) => e.blocking.length === 0 && e.warnings.length === 0) && (
                <div className="text-sm text-green-700 dark:text-green-300 flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4" /> No exceptions. {validation.employees.filter((e) => e.formRequired).length} form(s) required.
                </div>
              )}
              {validation.employer.length > 0 && (
                <div>
                  <div className="text-xs font-semibold uppercase text-muted-foreground mb-1">Employer</div>
                  <ul className="space-y-1">
                    {[...validation.employer].sort((a, b) => (a.severity === "blocking" ? 0 : 1) - (b.severity === "blocking" ? 0 : 1)).map((ex, i) => (
                      <li key={i} className="text-sm flex items-start gap-2">
                        <Explained term={ex.severity === "blocking" ? "exceptionBlocking" : "exceptionWarning"}><Badge variant={ex.severity === "blocking" ? "destructive" : ex.severity === "warning" ? "outline" : "secondary"} className="mt-0.5">{ex.severity}</Badge></Explained>
                        <span>{ex.message}{ex.fix && <span className="text-muted-foreground"> — fix in {FIX_AREA_LABEL[ex.fix.area] ?? ex.fix.area}{ex.fix.hint ? ` (${ex.fix.hint})` : ""}</span>}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {validation.employees.some((e) => e.blocking.length || e.warnings.length) && (
                <div>
                  <div className="text-xs font-semibold uppercase text-muted-foreground mb-1">Employees</div>
                  <div className="max-h-72 overflow-y-auto border rounded">
                    <table className="w-full text-sm">
                      <thead className="bg-muted sticky top-0"><tr><th className="text-left p-2">Employee</th><th className="text-left p-2"><TermLabel term="formRequired">Form</TermLabel></th><th className="text-left p-2"><TermLabel term="exceptionBlocking">Exceptions</TermLabel></th></tr></thead>
                      <tbody>
                        {[...validation.employees].filter((e) => e.blocking.length || e.warnings.length).sort((a, b) => b.blocking.length - a.blocking.length).map((e) => (
                          <tr key={e.employeeId} className="border-t align-top">
                            <td className="p-2 whitespace-nowrap">{e.name}</td>
                            <td className="p-2 whitespace-nowrap text-xs">{e.formRequired ? <Explained term={e.formRequired === "full_time" ? "formRequired" : "code1G"}>{e.formRequired === "full_time" ? "1095-C (full-time)" : "1095-C (self-insured)"}</Explained> : "—"}</td>
                            <td className="p-2">
                              <ul className="space-y-0.5">
                                {e.blocking.map((ex, i) => (
                                  <li key={`b${i}`} className="text-xs"><Explained term="exceptionBlocking"><span className="text-red-600 font-medium">Blocking</span></Explained> {ex.month ? `${MONTHS[ex.month - 1]}: ` : ""}{ex.message}{ex.fix && <span className="text-muted-foreground"> — {FIX_AREA_LABEL[ex.fix.area] ?? ex.fix.area}</span>}</li>
                                ))}
                                {e.warnings.map((ex, i) => (
                                  <li key={`w${i}`} className="text-xs"><Explained term="exceptionWarning"><span className="text-amber-600 font-medium">Warning</span></Explained> {ex.month ? `${MONTHS[ex.month - 1]}: ` : ""}{ex.message}{ex.fix && <span className="text-muted-foreground"> — {FIX_AREA_LABEL[ex.fix.area] ?? ex.fix.area}</span>}</li>
                                ))}
                              </ul>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              {lineConsistencyIssues.length > 0 && (
                <Alert variant="destructive">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription className="text-sm">
                    <TermLabel term="line15">Line 15</TermLabel> is missing for a Line 14 offer code that requires it: {lineConsistencyIssues.map((e) => `${e.name} (${e.lineConsistency.map((m) => MONTHS[m - 1]).join(", ")})`).join("; ")}.
                  </AlertDescription>
                </Alert>
              )}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                <div className="border rounded p-2"><div className="text-xs text-muted-foreground"><TermLabel term="ale">ALE status</TermLabel>{validation.ale.estimated && <Help term="aleEstimated" className="ml-1" />}</div><div className="font-medium">{validation.ale.isAle ? "ALE" : "Not an ALE"}{validation.ale.estimated ? " (estimated)" : ""}</div></div>
                <div className="border rounded p-2"><div className="text-xs text-muted-foreground"><TermLabel term="line18">1094-C Line 18 (forms)</TermLabel></div><div className="font-medium">{validation.form1094.line18}</div></div>
                <div className="border rounded p-2"><div className="text-xs text-muted-foreground"><TermLabel term="line18">1094-C reconciles with 1095-C count</TermLabel></div><div className="font-medium">{validation.form1094.reconciles ? "Yes" : "No — regenerate"}</div></div>
                <div className="border rounded p-2"><div className="text-xs text-muted-foreground"><TermLabel term="override">Active overrides</TermLabel></div><div className="font-medium">{overrides.length}</div></div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <CardTitle className="text-base"><TermLabel term="snapshotVersion">Form records{forms?.version ? ` — version ${forms.version}` : ""}</TermLabel></CardTitle>
            <div className="flex items-center gap-2">
              <Button variant={formView === "1095c" ? "default" : "outline"} size="sm" onClick={() => setFormView("1095c")}>1095-C ({records.length})</Button>
              <Help term="form1095c" />
              <Button variant={formView === "1094c" ? "default" : "outline"} size="sm" onClick={() => setFormView("1094c")}>1094-C</Button>
              <Help term="form1094c" />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {formsLoading && <div className="text-sm text-muted-foreground">Loading…</div>}
          {!formsLoading && (!forms || forms.version === 0) && (
            <div className="text-center py-10 text-muted-foreground">
              <FileText className="h-10 w-10 mx-auto mb-3 opacity-50" />
              <p className="font-medium">No form records for {taxYear} yet</p>
              <p className="text-sm">Resolve blocking exceptions, then use "Generate forms".</p>
            </div>
          )}
          {forms && forms.version > 0 && formView === "1095c" && (
            records.length === 0 ? (
              <div className="text-center py-10 text-muted-foreground">
                <p className="font-medium">No 1095-C required for {taxYear}</p>
                <p className="text-sm">No employee was ACA full-time in any month or enrolled in self-insured coverage.</p>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <Select value={String(selected?.record.employeeId ?? "")} onValueChange={setSelectedEmployeeId}>
                    <SelectTrigger className="w-[320px]"><SelectValue placeholder="Select employee" /></SelectTrigger>
                    <SelectContent>
                      {records.map(({ record, employee }) => (
                        <SelectItem key={record.employeeId} value={String(record.employeeId)}>
                          {employee ? `${employee.firstName} ${employee.lastName}` : `Employee #${record.employeeId}`} · {record.requiredReason === "self_insured_enrolled" ? "self-insured" : "full-time"}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Button variant="outline" size="icon" onClick={() => goTo(-1)} disabled={currentIndex <= 0}><ChevronLeft className="h-4 w-4" /></Button>
                    <span className="text-sm text-muted-foreground">{currentIndex + 1} of {records.length}</span>
                    <Button variant="outline" size="icon" onClick={() => goTo(1)} disabled={currentIndex >= records.length - 1}><ChevronRight className="h-4 w-4" /></Button>
                    {selected && (
                      <>
                        <Button variant="outline" size="sm" onClick={() => window.open(`/api/aca/forms/1095c/${selected.record.employeeId}/preview.pdf?${q}`, "_blank")}>
                          <ExternalLink className="h-4 w-4 mr-1" /> Preview PDF
                        </Button>
                        <Button size="sm" onClick={() => savePdfMutation.mutate({ kind: "1095c", employeeId: selected.record.employeeId })} disabled={savePdfMutation.isPending}>
                          {savePdfMutation.isPending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Download className="h-4 w-4 mr-1" />} Save PDF to documents
                        </Button>
                      </>
                    )}
                  </div>
                </div>
                {selected && (() => {
                  const saved = pdfFor(selected.record.employeeId, "1095c");
                  return saved?.pdfUrl ? (
                    <p className="text-xs text-muted-foreground">Last saved PDF: v{saved.version} · {new Date(saved.generatedAt).toLocaleString()} · <a className="underline" href={saved.pdfUrl} target="_blank" rel="noreferrer">open</a></p>
                  ) : null;
                })()}
                {selected && client && (
                  <Form1095C
                    record={selected.record}
                    employee={selected.employee ?? { id: selected.record.employeeId, firstName: "", lastName: "" }}
                    client={client}
                    taxYear={taxYear}
                    codes14={constants?.line14Codes}
                    codes16={constants?.line16Codes}
                  />
                )}
                {selected && (
                  <div className="border rounded p-3">
                    <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
                      <div className="text-sm font-medium"><TermLabel term="override">Audited overrides for this employee</TermLabel></div>
                      <div className="flex gap-2 flex-wrap items-center">
                        <Button size="sm" variant="outline" disabled={isLocked} onClick={() => openOverride("line14", selected.record.employeeId, 1, selected.record.line14?.["1"] ?? "")}>Line 14</Button><Help term="line14" />
                        <Button size="sm" variant="outline" disabled={isLocked} onClick={() => openOverride("line15", selected.record.employeeId, 1, selected.record.line15?.["1"] == null ? "" : String(selected.record.line15?.["1"]))}>Line 15</Button><Help term="line15" />
                        <Button size="sm" variant="outline" disabled={isLocked} onClick={() => openOverride("line16", selected.record.employeeId, 1, selected.record.line16?.["1"] ?? "")}>Line 16</Button><Help term="line16" />
                        <Button size="sm" variant="outline" disabled={isLocked} onClick={() => openOverride("plan_start_month", selected.record.employeeId, null, selected.record.planStartMonth ?? "")}>Plan start month</Button><Help term="planStartMonth" />
                      </div>
                    </div>
                    {isLocked && <p className="text-xs text-muted-foreground mb-2">Year is finalized. Unlock it from History to record corrections (a new version will be generated).</p>}
                    <OverrideList rows={overrides.filter((o) => !o.revokedAt && o.employeeId === selected.record.employeeId)} onRevoke={(id) => revokeMutation.mutate(id)} disabled={isLocked || revokeMutation.isPending} />
                  </div>
                )}
              </div>
            )
          )}
          {forms && forms.version > 0 && formView === "1094c" && (
            forms.transmittal && client ? (
              <div className="space-y-4">
                <div className="flex items-center justify-end gap-2">
                  <Button variant="outline" size="sm" onClick={() => window.open(`/api/aca/forms/1094c/preview.pdf?${q}`, "_blank")}><ExternalLink className="h-4 w-4 mr-1" /> Preview PDF</Button>
                  <Button size="sm" onClick={() => savePdfMutation.mutate({ kind: "1094c" })} disabled={savePdfMutation.isPending}>
                    {savePdfMutation.isPending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Download className="h-4 w-4 mr-1" />} Save PDF to documents
                  </Button>
                </div>
                {(() => {
                  const saved = pdfFor(null, "1094c");
                  return saved?.pdfUrl ? <p className="text-xs text-muted-foreground">Last saved PDF: v{saved.version} · {new Date(saved.generatedAt).toLocaleString()} · <a className="underline" href={saved.pdfUrl} target="_blank" rel="noreferrer">open</a></p> : null;
                })()}
                <Form1094C transmittal={forms.transmittal} client={client} taxYear={taxYear} members={groupMembers} />
                <div className="border rounded p-3">
                  <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
                    <div className="text-sm font-medium"><TermLabel term="override">Audited overrides — monthly counts</TermLabel></div>
                    <div className="flex gap-2 flex-wrap items-center">
                      <Button size="sm" variant="outline" disabled={isLocked} onClick={() => openOverride("ft_count", null, 1, String(forms.transmittal?.monthlyFtCount?.["1"] ?? ""))}>Full-time count</Button><Help term="ftCount1094" />
                      <Button size="sm" variant="outline" disabled={isLocked} onClick={() => openOverride("total_count", null, 1, String(forms.transmittal?.monthlyTotalCount?.["1"] ?? ""))}>Total count</Button><Help term="totalCount1094" />
                      <Button size="sm" variant="outline" disabled={isLocked} onClick={() => openOverride("mec_offered", null, 1, String(forms.transmittal?.monthlyMecOffered?.["1"] ?? "true"))}>MEC offered</Button><Help term="mecOfferIndicator" />
                    </div>
                  </div>
                  <OverrideList rows={overrides.filter((o) => !o.revokedAt && o.employeeId == null)} onRevoke={(id) => revokeMutation.mutate(id)} disabled={isLocked || revokeMutation.isPending} />
                </div>
              </div>
            ) : (
              <div className="text-center py-10 text-muted-foreground">
                <p className="font-medium">No 1094-C record yet</p>
                <p className="text-sm">Generate forms to build the transmittal from the same monthly results.</p>
              </div>
            )
          )}
        </CardContent>
      </Card>

      <Dialog open={overrideOpen} onOpenChange={setOverrideOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle><TermLabel term="override">Audited override — {fieldLabel(override.field)}</TermLabel></DialogTitle>
            <DialogDescription>
              Records who changed it, when, why, the prior value and the new value. The year is recalculated immediately; generate forms again afterwards.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {override.field !== "plan_start_month" && (
              <div>
                <Label>Month</Label>
                <Select value={String(override.month ?? 1)} onValueChange={(v) => setOverride((o) => ({ ...o, month: Number(v) }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{MONTHS.map((m, i) => <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label>
                <TermLabel term={override.field === "line14" ? "line14" : override.field === "line15" ? "line15" : override.field === "line16" ? "line16" : override.field === "plan_start_month" ? "planStartMonth" : override.field === "ft_count" ? "ftCount1094" : override.field === "total_count" ? "totalCount1094" : "mecOfferIndicator"}>New value</TermLabel>
              </Label>
              {override.field === "line14" || override.field === "line16" ? (
                <Select value={override.newValue || "__blank"} onValueChange={(v) => setOverride((o) => ({ ...o, newValue: v === "__blank" ? "" : v }))}>
                  <SelectTrigger><SelectValue placeholder="Code" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__blank">(blank)</SelectItem>
                    {Object.entries((override.field === "line14" ? constants?.line14Codes : constants?.line16Codes) ?? {}).map(([code, desc]) => (
                      <SelectItem key={code} value={code}>{code} — {String(desc).slice(0, 80)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : override.field === "mec_offered" ? (
                <Select value={override.newValue || "true"} onValueChange={(v) => setOverride((o) => ({ ...o, newValue: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="true">Yes</SelectItem><SelectItem value="false">No</SelectItem></SelectContent>
                </Select>
              ) : override.field === "plan_start_month" ? (
                <Select value={override.newValue || "00"} onValueChange={(v) => setOverride((o) => ({ ...o, newValue: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{["00", ...MONTHS.map((_, i) => String(i + 1).padStart(2, "0"))].map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
                </Select>
              ) : (
                <Input type="number" step={override.field === "line15" ? "0.01" : "1"} min={0} value={override.newValue} onChange={(e) => setOverride((o) => ({ ...o, newValue: e.target.value }))} placeholder={override.field === "line15" ? "0.00 (blank clears)" : ""} />
              )}
            </div>
            <div>
              <Label><TermLabel term="override">Reason (required)</TermLabel></Label>
              <Textarea value={override.reason} onChange={(e) => setOverride((o) => ({ ...o, reason: e.target.value }))} placeholder="Why the calculated value is wrong for this month" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOverrideOpen(false)}>Cancel</Button>
            <Button onClick={() => overrideMutation.mutate()} disabled={overrideMutation.isPending || override.reason.trim().length < 3}>
              {overrideMutation.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}Record override
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function OverrideList({ rows, onRevoke, disabled }: { rows: OverrideRow[]; onRevoke: (id: number) => void; disabled: boolean }) {
  if (rows.length === 0) return <p className="text-xs text-muted-foreground">No active overrides.</p>;
  return (
    <table className="w-full text-xs">
      <thead>
        <tr className="text-muted-foreground">
          <th className="text-left p-1">Field</th><th className="text-left p-1">Month</th><th className="text-left p-1"><TermLabel term="override">Prior</TermLabel></th><th className="text-left p-1"><TermLabel term="override">New</TermLabel></th><th className="text-left p-1">Reason</th><th className="text-left p-1">By / when</th><th></th>
        </tr>
      </thead>
      <tbody>
        {rows.map((o) => (
          <tr key={o.id} className="border-t">
            <td className="p-1">{fieldLabel(o.field)}</td>
            <td className="p-1">{o.month ? MONTHS[o.month - 1] : "—"}</td>
            <td className="p-1 font-mono">{o.priorValue ?? "—"}</td>
            <td className="p-1 font-mono">{o.newValue ?? "(blank)"}</td>
            <td className="p-1">{o.reason}</td>
            <td className="p-1 text-muted-foreground">{o.createdByUserId ?? "—"} · {new Date(o.createdAt).toLocaleString()}</td>
            <td className="p-1 text-right"><Button size="sm" variant="ghost" disabled={disabled} onClick={() => onRevoke(o.id)} title="Revoke"><Undo2 className="h-3 w-3" /></Button></td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
